import * as React from 'react';
import styled from '@emotion/styled';
import { useStaticQuery, graphql } from 'gatsby';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GatsbySite } from '../../graphql';
import SEO from '../../components/seo';
import ResponsiveDiv from '../../components/utility/ResponsiveDiv';
import TitlePage from '../../components/project/TitlePage';
import Client from '../../components/project/client';
import Section from '../../components/project/section';
import Quote from '../../components/project/quote';
import ResponsiveParagraph from '../../components/utility/ResponsiveParagraph';
import { SectionTag } from '../../components/project/section-tag';
import Tech from '../../components/project/tech';
import { CenteredContent } from '../../components/utility/section';
import { SpacingWrapper } from '../../components/utility/spacing-wrapper';
import { HideOnMobile } from '../../components/utility/hide-on';
import Video from '../../components/video';
import Carousel from '../../components/project/carousel';
import NextMissions from '../../components/project/next-missions';
// import photos and videos
import heroVideo from '../../images/projects/risk-alive/hero-video.mp4';
import background from '../../images/projects/enjoyn/background.png';
import logo from '../../images/projects/risk-alive/logo.png';
import laptop from '../../images/projects/risk-alive/laptop.png';
import carousel2 from '../../images/projects/risk-alive/carousel2.png';
import carousel3 from '../../images/projects/risk-alive/carousel3.png';
import carousel4 from '../../images/projects/risk-alive/carousel4.png';
import carousel5 from '../../images/projects/risk-alive/carousel5.png';
import carousel6 from '../../images/projects/risk-alive/carousel6.png';
import alberio from '../../images/projects/employee-avatars/alberio-bathory-frota.png';
import crudeHeater from '../../images/projects/risk-alive/crude-heater.png';
import reactLogo from '../../images/projects/risk-alive/react-logo.png';
import unityLogo from '../../images/projects/risk-alive/unity-logo.png';
import riskReduction from '../../images/projects/risk-alive/risk-reduction.png';
import library from '../../images/projects/risk-alive/library.png';

const RiskAlivePage = () => {
  const { site } = useStaticQuery<GatsbySite>(
    graphql`
      query {
        site {
          siteMetadata {
            projects {
              name
              description
              imageName
              link
            }
          }
        }
      }
    `,
  );

  const { siteMetadata } = site;

  const projects = siteMetadata?.projects || [];
  return (
    <>
      <SEO title="Risk Alive" />
      <div id="#top" />
      <TitlePage
        title="Risk Alive: A data visualization tool with safety at its heart"
        content="Making sense of risks and process safety management through data and productization of knowledge. If that does not get you going, we even whipped up some gameplay in Unity gaming engine! "
        backgroundImage={background}
      />
      <SpacingWrapper>
        <Client
          content={`Simply put, Risk Alive Analytics has the world's largest repository of risk data. Over the years, the company has gathered data on various equipment, processes, and safety events, across industries, including oil and gas, manufacturing, etc. This allows them to consult on process safety management issues, predict hazardous events, repair schedules, and save lives and resources.`}
          images={[{ image: logo, url: 'https://www.riskalive.com/' }]}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <HideOnMobile breakpoint={1024}>
          <Video title="A view of the menu" src={heroVideo} />
        </HideOnMobile>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`Risk and process safety management are essential to the industry at large. Manufacturers, processing plants and work sites across industries, including oil and gas, utilities, construction, etc. have had to deal with accidents and hazards for as long as they have existed. 
          To put it in perspective, in 2020, more than 250,000 workplace-related accidents/events were accepted by Workers' Compensation Boards across Canada¹. These incidents and/or occupation-related diseases have resulted in as many as 921 fatalities.      
          According to some estimates, while fire and explosion account for only 3 percent of workplace injuries, they have the highest casualty rates among workplace incidents².`}
          p2={`Process Hazard Analysis (PHA), as the name suggests, refers to a series of studies conducted in workplaces to identify potential hazards and incidents. Hazard and Operability study (HAZOP) is one of the many methods to conduct a PHA. 
          Risk Alive has millions of PHA/HAZOP learnings in their repository and provides its clients/companies access to accurately predict potential hazards based on this information.     
          The Risk Alive Library productized these thorough learnings and studies, conducted over the years, allowing companies to make informed decisions about risk and process safety management. `}
          heading="Low risk, high gain!"
          subHeading="summary"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img src={laptop} alt="Laptop displaying the chat function" style={{ width: '100%' }} />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`Risk Alive Analytics has more PHA/HAZOP learnings than anyone else, with data spanning sectors like oil and gas, petrochemicals, mining, food and beverage, etc.
          In most cases, any regulatory-compliant risk assessment effort on the part of companies tends to look at existing maintenance and other data, and day-to-day operations to pinpoint potential hazards and risks.    
          But what if companies had the benefit of a massive repository of data from others in the same industry, who are using similar equipment and do the same kind of work? `}
          p2={`Risk Alive facilitates exactly that, with predictive analytics, including trends and averages, hardware wear and tear, optimum maintenance efforts, among other things. 
          However, Risk Alive wanted to productize this knowledge and the massive repository of learnings, elevating their profile from being just a service provider.       
          Given that safety manuals and data are not exactly bestselling page-turners (amine surge tanks, anyone?), easy-to-use data visualization and creative innovation technology had to be deployed. `}
          heading="How to productize process safety management knowledge?"
          subHeading="pain"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Quote
          quote="Risk Alive has a vast repository of valuable risk and safety data. Productizing that knowledge with a solution that works efficiently was the challenge. Adding gameplay, creating an online twin of one of the world's most beautiful libraries, just made it that much more interesting of a project."
          citation="Alberio Bathory-Frota"
          citation2="CEO @ Launchcode"
          picture={alberio}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>vision</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>Data visualization & innovation technology</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              A web-based platform that could simplify complex data, encourage client usage and user
              interest, implement dynamic user experience depending on the user profile (engineer,
              plant manager, process engineer, etc.). And if the ideal solution can speak to the
              administration and jargon-spewing on-floor engineers alike, that would be a cherry on
              top.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={crudeHeater}
              alt="Data for a crude heater charge pump"
              style={{ maxWidth: 1040, width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Tech
          content={`React allows for solutions to be responsive, which means they can be run on different screen sizes and platforms without having to nurture the code and the development process.
          From the touchscreen-enabled hardware that connected to the existing fleet of lathes and mills, to the management’s mobile screen, the stack would allow for real-time monitoring in the actual sense of the time. `}
          images={[reactLogo, unityLogo]}
          subtitle="Unity gaming engine & co."
          subtitleFontSize={42}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`The Risk Alive project commanded a deep dive into the world of risk assessment and process safety management. After all, amine surge tanks, chemical compositions, hardware wear and tear, are not everyone's expertise. 
          The deep dive was also about understanding what the most ideal scenario/method would be to present data and keep it interesting at the same time. 
          The next step was to give Risk Alive's clients a teaser into the kind of in-depth and detailed information they are dealing with. 
          A slot machine game was created. The user, if they got all the answers right about specific unit safeguards, would be scored on the number of attempts. The game not only ensures that it makes the stakeholders think about the safeguards in their respective units, but at the same time also creates a playful competitive spirit. 
          It shows they are gambling with risks if they don't get it right. `}
          p2={`Having given the client (or a potential client) a taste of how Risk Alive focuses on simplifying information about safeguards, the next step was to design a product platform that would help users get specific information and data on hardware, processes, and safeguards. 
          After considering various user interfaces, an online representation of a library was finalized on. Not just any library would have done though. It had to be beautiful and inviting. 
          Eventually Stuttgart City Library made the cut. The nine-floor cube monolith is among the world's most photographed structures. The symmetry on the outside is matched by an inverted pyramid shape on the inside with flowing stairways taking you further on your journey of knowledge. 
          Unity gaming engine was used to create an online twin of the Stuttgart library, with multiple user avatars in quest of knowledge. 
          Books and shelves in the library would be dynamic and change depending on the user avatars. 
           `}
          heading="How to combine technology with data visualization & safety"
          subHeading="journey"
        />
        <SpacingWrapper>
          <Carousel
            images={[
              { image: carousel5, alt: 'Welcome to the Risk Alive library' },
              { image: carousel3, alt: 'Build your character' },
              { image: carousel4, alt: 'View of personal library' },
              { image: carousel2, alt: 'Launchcode Canada book in Risk Alive library' },
              { image: carousel6, alt: 'Book suggestions for you' },
              { image: riskReduction, alt: 'Chart showing risk reduction' },
            ]}
          />
        </SpacingWrapper>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Given the integration of gameplay and characters, it was important that the platform did not look or feel gimmicky. 
          Gameplay was to only initiate some friendly competitive spirit within the client organization, while also ensuring knowledge sharing and process safety management. 
          Launchcode developers also had to pay attention to the data visualization aspect of the project. Simplifying was key to platform productization, but oversimplification could potentially alienate the more knowledgeable users of the platform.
           `}
          p2={`There was a fine line between making the platform user-friendly for a wider demographic and making it obsolete for advanced users. 
          The middle path eventually was to create a Stuttgart City Library-inspired online setup that would interest a wider demographic, and the knowledge gained at the end of a session was simplified enough for all. 
           `}
          heading="What you see is what you get"
          subHeading="ux/ui"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`The multiple sprints during the various phases of development ensured that some of the biggest pains and questions were answered. 
          From a marketing perspective for Risk Alive, the slots machine game gave a nice little teaser to current and prospective clients about potential hazards and maintenance events that could arise from their respective plants.    
          The product - library - that emerged out of the development plan gave Risk Alive an edge and built a momentum off of the slots game. `}
          p2={`It had multiple user profiles and characters, various data visualization options, and a compelling and intuitive user interface. 
          The library had impressionist traits, like simpler graphics, rather than the realism of Stuttgart library's grandeur, baked in to ensure speed and faster loading times. It gave a very playful character to the platform, something that was appreciated.`}
          heading="Data visualization made easy"
          subHeading="results"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={library}
              alt="A desktop computer displaying the Risk Alive library"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Quote
          quote="It's a really cool way to search for information. The younger members of our team really enjoy it. It's cartoonish and really playful... They're (Launchcode) easy to talk to and they always provide us options to achieve our goals."
          citation="Fernando Moreira"
          citation2="Risk Alive Research and Development Manager "
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`An intense run of testing was held, with data from a small group of companies first released onto the platform, before the entire repository was added. 
          With increased usage of and additions to the database, the library grew in relevancy and credibility.       
          The solution was well-received by all stakeholders, and the library empowered employees to minimize risk at their facilities while also ensuring a little bit of healthy competition through gameplay.`}
          p2={`'Fun' and 'engaging' are some of the adjectives being used for the experience that facilitates learning despite the complexities of data and information at hand.  
          Anticipation of events and risks has increased manifold among clients of Risk Alive, with sharing of data from the repository allowing decision-making to be data-backed. `}
          heading="Clients love us!"
          subHeading="response"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <CitationsTitleWrapper>
            <h2
              style={{
                fontWeight: 500,
                fontSize: 48,
                maxWidth: 730,
                textAlign: 'left',
              }}
            >
              <strong>Citations</strong>
            </h2>
          </CitationsTitleWrapper>
        </ResponsiveDiv>

        <ResponsiveDiv>
          <ResponsiveParagraph>
            <p>
              <sup>1</sup>
              <a
                href="https://awcbc.org/en/statistics/national-work-injury-disease-and-fatality-statistics-nwisp-year-at-a-glance/"
                target="_blank"
                rel="nofollow noreferrer"
              >
                “National Work Injury, Disease and Fatality Statistics (NWISP) - Year At A Glance”.
              </a>{' '}
              Association of Workers' Compensation Boards of Canada. Accessed 24 May 2022.{' '}
            </p>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p>
              <sup>2</sup>
              <a
                href="https://www.thesafetymag.com/ca/topics/safety-and-ppe/the-five-most-common-types-of-workplace-injuries/316462"
                target="_blank"
                rel="nofollow noreferrer"
              >
                Foulis, Maia. “The five most common types of workplace injuries”.
              </a>{' '}
              The Safety Mag, 15 November 2021. Accessed 1 June 2022.{' '}
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <NextMissions currentProjectName="Risk Alive" projects={projects} />

      <CenteredContent style={{ marginBottom: 40 }}>
        <a href="#top">Back to top ⇮</a>
      </CenteredContent>
    </>
  );
};

const SimpleUXImageWrapper = styled.div`
  > div {
    overflow: unset;
    max-width: 70ch;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      top: 12.5%;
    }
  }
`;

const CitationsTitleWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: 40px;
  }

  @media screen and (min-width: 1250px) {
    margin-left: 40px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.wideScreen}) {
    margin-left: 40px;
  }
`;

export default RiskAlivePage;
